import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LoyaltyStayUpgradeAccordionSection from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeAccordionSection'
import LoyaltyStayUpgradeDetailsModal from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeDetailsModal'
import LoyaltyStayUpgradeSectionContent from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeSectionContent'
import LoyaltyTierBenefitAvailabilityMessageBanner from 'loyalty/components/LoyaltyTierBenefitAvailabilityMessageBanner'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { useAppSelector } from 'hooks/reduxHooks'
import useLuxPlusPricingContext from 'hooks/useLuxPlusPricingContext'
import getLoyaltyUpgradePackagePayablePrice from 'loyalty/lib/benefits/getLoyaltyUpgradePackagePayablePrice'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { getPackageViews } from 'selectors/offerPage/offerPageSelectors'

/**
 * Prices from the package's dated view when one exists — `hotelTotal`/`hotelMemberTotal` are what
 * the rate options display for the selected dates. The raw package's `price`/`memberPrice` are the
 * offer payload's undated lead-in, kept only as a fallback for a package with no view.
 */
function getPackageViewPayablePrice(
  view: App.OfferPackageOptionView | undefined,
  fallbackPackage: App.HotelPackage,
  pricingContext: Parameters<typeof getLoyaltyUpgradePackagePayablePrice>[1],
): number {
  return getLoyaltyUpgradePackagePayablePrice(
    view ? { price: view.hotelTotal, memberPrice: view.hotelMemberTotal } : fallbackPackage,
    pricingContext,
  )
}

interface Props {
  originPackage: App.HotelPackage
  /**
   * Any availability shape — a package option offering only one kind of upgrade renders a single
   * section rather than an empty one.
   */
  upgradeAvailabilityView: App.OfferPackageOptionLoyaltyUpgradeAvailabilityView
  selectedTargetUpgradeType: App.LoyaltyStayUpgradeType | undefined
  /** Opens with this section already expanded — set when arriving from that upgrade's row. */
  initiallyExpandedUpgradeType?: App.LoyaltyStayUpgradeType
}

interface Result {
  upgradeType: App.LoyaltyStayUpgradeType | undefined
}

export type OfferPageLoyaltyStayUpgradeModalResult = Result | undefined

function OfferPageLoyaltyStayUpgradeModal({ originPackage, upgradeAvailabilityView, selectedTargetUpgradeType, initiallyExpandedUpgradeType }: Props) {
  const modalElement = useModalElementContext<OfferPageLoyaltyStayUpgradeModalResult>()
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const offerPageState = useContext(OfferPageStateContext)
  const { offer, checkInDate, checkOutDate } = offerPageState
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const upgradeBenefit = useLoyaltyUserBenefitView('hotel_upgrade')
  const pricingContext = useLuxPlusPricingContext()

  const packageTarget = 'packageUpgradePackage' in upgradeAvailabilityView ? upgradeAvailabilityView.packageUpgradePackage : undefined
  const roomTarget = 'roomUpgradePackage' in upgradeAvailabilityView ? upgradeAvailabilityView.roomUpgradePackage : undefined

  // The same dated views the rate options price from, covering every package on the offer — the
  // upgrade targets included. With no dates selected these fall back to the lead-in price, so the
  // modal always shows the same figures as the tiles.
  const selectPackageViews = useMemo(() => (appState: App.State) => getPackageViews(offerPageState, appState), [offerPageState])
  const packageViews = useAppSelector(selectPackageViews)
  const originView = packageViews.find(view => view.package.uniqueKey === originPackage.uniqueKey)
  const packageTargetView = packageTarget ? packageViews.find(view => view.package.uniqueKey === packageTarget.uniqueKey) : undefined
  const roomTargetView = roomTarget ? packageViews.find(view => view.package.uniqueKey === roomTarget.uniqueKey) : undefined

  // What the member pays either way: the source package's price. The upgrade is free, so the only
  // thing that moves is which package they get — never the amount charged.
  const yourPrice = getPackageViewPayablePrice(originView, originPackage, pricingContext)
  const packageStandardPrice = packageTarget ? getPackageViewPayablePrice(packageTargetView, packageTarget, pricingContext) : 0
  const roomStandardPrice = roomTarget ? getPackageViewPayablePrice(roomTargetView, roomTarget, pricingContext) : 0

  // Sections open independently — a member may want both expanded to compare. Both start collapsed.
  const [expandedSections, setExpandedSections] = useState<Set<App.LoyaltyStayUpgradeType>>(
    () => initiallyExpandedUpgradeType ? new Set([initiallyExpandedUpgradeType]) : new Set(),
  )
  const toggleSection = useCallback((upgradeType: App.LoyaltyStayUpgradeType) => {
    setExpandedSections((current) => {
      const next = new Set(current)
      if (next.has(upgradeType)) next.delete(upgradeType)
      else next.add(upgradeType)
      return next
    })
  }, [])

  const togglePackageSection = useCallback(() => toggleSection('package'), [toggleSection])
  const toggleRoomSection = useCallback(() => toggleSection('room'), [toggleSection])

  // The full room detail stays a separate modal — it carries a gallery, description and policies that
  // would swamp the accordion. Stacks over this one rather than replacing it.
  const showRoomDetails = useCallback(() => {
    if (!roomTarget) return
    showModal(<LoyaltyStayUpgradeDetailsModal
      upgradeType="room"
      originPackage={originPackage}
      targetPackage={roomTarget}
      targetValue={roomTargetView?.pricing.value}
      offer={offer}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      hasBackAction
    />)
  }, [showModal, roomTarget, roomTargetView, originPackage, offer, checkInDate, checkOutDate])

  const applyPackageUpgrade = useCallback(() => modalElement.resolve({ upgradeType: 'package' }), [modalElement])
  const applyRoomUpgrade = useCallback(() => modalElement.resolve({ upgradeType: 'room' }), [modalElement])
  const removeUpgrade = useCallback(() => modalElement.resolve({ upgradeType: undefined }), [modalElement])

  const isInfiniteTier = tier === 'infinite'
  const tierDisplayName = getLoyaltyTierDisplayName(tier)
  const programDisplayName = getLoyaltyProgramDisplayName()

  let modalTitle: string
  if (isInfiniteTier) {
    modalTitle = intl.formatMessage(
      {
        id: 'XHvWdfv_aJIincpORhEn',
        defaultMessage: 'Free Upgrades with {tierName}',
        description: 'Title of the offer page modal where a loyalty member applies their free package upgrade',
      },
      { tierName: tierDisplayName },
    )
  } else {
    modalTitle = intl.formatMessage(
      {
        id: 'UUi2MFSMrTusAI-pFZAB',
        defaultMessage: 'Free Upgrades with {program} {tierName}',
        description: 'Title of the offer page modal where a loyalty member of a named program tier applies their free package upgrade',
      },
      { program: programDisplayName, tierName: tierDisplayName },
    )
  }

  // An unlimited allowance has no number to quote, so it needs its own sentence rather than a count —
  // the count falls back to zero and reads "You receive 0 free upgrades", contradicting the bar right
  // beneath it. Keyed off the benefit's own availability, as SPP does, not off the tier name.
  const isUnlimited = upgradeBenefit?.availability === Infinity

  let allowanceMessage: string
  if (isUnlimited) {
    allowanceMessage = intl.formatMessage(
      {
        id: 'XmECy5UNaZybrAepkNWN',
        defaultMessage: 'You receive unlimited free upgrades as an {tierName} member.',
        description: 'Explains that a top tier loyalty member receives unlimited free package upgrades',
      },
      { tierName: tierDisplayName },
    )
  } else if (isInfiniteTier) {
    allowanceMessage = intl.formatMessage(
      {
        id: '4rAwxni7KBEEz_FBVcg0',
        defaultMessage: 'You receive {count, plural, one {# free upgrade} other {# free upgrades}} each membership year as an {tierName} member.',
        description: 'Explains how many free package upgrades a loyalty member receives each membership year',
      },
      { count: upgradeBenefit?.benefit.limit ?? 0, tierName: tierDisplayName },
    )
  } else {
    allowanceMessage = intl.formatMessage(
      {
        id: 'NxOCU8ye-ngyl2xOy_S9',
        defaultMessage: 'You receive {count, plural, one {# free upgrade} other {# free upgrades}} each membership year as a {program} {tierName} member.',
        description: 'Explains how many free package upgrades a loyalty member of a named program tier receives each membership year',
      },
      { count: upgradeBenefit?.benefit.limit ?? 0, program: programDisplayName, tierName: tierDisplayName },
    )
  }

  return <Modal title={modalTitle} size="S">
    <Group direction="vertical" gap={16}>
      <BodyText variant="large" colour="neutral-two">{allowanceMessage}</BodyText>

      <LoyaltyTierBenefitAvailabilityMessageBanner
        benefitType="hotel_upgrade"
        availabilityDisplay="upgrades-remaining"
        icon={<LoyaltyTierIcon tier={tier} />}
        iconSurface={false}
        flat
      />

      <BodyText variant="large" colour="neutral-one">
        {intl.formatMessage({
          id: 'zqa7Gwt_G-htJAmTNyIf',
          defaultMessage: 'Below are your available upgrades:',
          description: 'Lead-in above the list of package upgrades a loyalty member can apply to their booking',
        })}
      </BodyText>

      {!!packageTarget && <LoyaltyStayUpgradeAccordionSection
        upgradeType="package"
        targetName={packageTarget.name}
        isExpanded={expandedSections.has('package')}
        onToggle={togglePackageSection}
        onApply={applyPackageUpgrade}
        isApplied={selectedTargetUpgradeType === 'package'}
        onRemove={removeUpgrade}
        tier={tier}
        standardPrice={packageStandardPrice}
        yourPrice={yourPrice}
      >
        <LoyaltyStayUpgradeSectionContent
          upgradeType="package"
          targetPackage={packageTarget}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          tier={tier}
        />
      </LoyaltyStayUpgradeAccordionSection>}

      {!!roomTarget && <LoyaltyStayUpgradeAccordionSection
        upgradeType="room"
        targetName={roomTarget.roomType?.name ?? roomTarget.name}
        isExpanded={expandedSections.has('room')}
        onToggle={toggleRoomSection}
        onApply={applyRoomUpgrade}
        isApplied={selectedTargetUpgradeType === 'room'}
        onRemove={removeUpgrade}
        tier={tier}
        standardPrice={roomStandardPrice}
        yourPrice={yourPrice}
      >
        <LoyaltyStayUpgradeSectionContent
          upgradeType="room"
          targetPackage={roomTarget}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          tier={tier}
          onViewRoomDetails={showRoomDetails}
        />
      </LoyaltyStayUpgradeAccordionSection>}
    </Group>
  </Modal>
}

export default OfferPageLoyaltyStayUpgradeModal
